import * as React from 'react';
import * as styles from './SyncPanel.css';
import { IAPanel } from '../../../panels/Panel';
import { IADropDown } from '../../../dropDown/DropDown';
import CategoryService from '../../../../services/CategoryService';
import { IAButton } from '../../../button/Button';
import { LocalizationService } from '../../../../services/LocalizationService';
import { IEnvironment } from '../../../../interfaces/IEnvironment';
import { IASpinner } from '../../../spinner/Spinner';

export interface IProps {
  show: boolean;
  environment: IEnvironment;
  tenant: string;
  token: string;
  instance: string;
  highlightColor: string;
  site: string;
  application: string;
  selectedTermGroup?: string;
  selectedTermSet?: string;
  onDismiss: () => void;
  reload: () => void;
}

export interface IState {
  isLoading: boolean;
  isSynching: boolean;
  termGroupList: any[];
  selectedTermGroup?: string;
  selectedTermSet?: string;
}

export class IASyncPanel extends React.Component<IProps, IState> {
  private readonly localizationService = new LocalizationService();

  private termGroupOptions: { "key": string, "value": string, "disabled"?: boolean }[];
  private termSetOptions: { "key": string, "value": string, "disabled"?: boolean }[];

  constructor(props: IProps) {
    super(props);
    this.state = {
      isLoading: false,
      isSynching: false,
      termGroupList: undefined,
      selectedTermGroup: props.selectedTermGroup,
      selectedTermSet: props.selectedTermSet
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentWillReceiveProps(props: IProps) {
    if (props.show !== this.props.show) {
      this.loadTerms();
    }
    if (props !== this.props) {
      this.setState({
        selectedTermGroup: props.selectedTermGroup,
        selectedTermSet: props.selectedTermSet
      });
    }
  }

  private loadTerms(): void {
    this.setState({ isLoading: true });
    CategoryService.getTermsAvailable(this.props.environment, this.props.application === "Messages" ? "IntraActive-Messages" : "IntraActive-Events", this.props.tenant, this.props.token).then(termAvailable => {
      this.termGroupOptions = [];
      this.termSetOptions = [];
      this.termGroupOptions.push({ key: " ", value: " " });
      termAvailable.forEach(item => {
        if (item.termSets !== undefined && item.termSets.length > 0) {
          this.termGroupOptions.push({ key: item.name, value: item.name });
        }
      });
      this.termGroupOptions = this.termGroupOptions.sort((x, y) => {
        if (x.value > y.value) {
          return 1;
        }
        if (x.value < y.value) {
          return -1;
        }
        return 0;
      });
      this.setState({ termGroupList: termAvailable, isLoading: false }, () => {
        this.loadTermSetsFromGroup(this.state.selectedTermGroup);
        this.render();
      });
    });
  }

  private loadTermSetsFromGroup(selectedGroup: string) {
    this.termSetOptions = [];
    if (selectedGroup !== " ") {
      let termGroupSelected = this.state.termGroupList.filter(x => x.name === selectedGroup)[0];
      if (termGroupSelected) {
        termGroupSelected.termSets.forEach(x => {
          this.termSetOptions.push({ value: x.name, key: x.name });
        });
      }
    } else {
      this.termSetOptions.push({ key: " ", value: " " });
    }
    this.termSetOptions = this.termSetOptions.sort((x, y) => {
      if (x.value > y.value) {
        return 1;
      }
      if (x.value < y.value) {
        return -1;
      }
      return 0;
    });
    if (this.state.selectedTermSet !== undefined) {
      let selectedKey = this.termSetOptions.filter(x => x.value === this.state.selectedTermSet)[0];
      if (selectedKey) {
        this.setState({ selectedTermSet: selectedKey.value });
      }
    } else {
      if (this.termSetOptions[0] != undefined) {
        this.setState({ selectedTermSet: this.termSetOptions[0].key });
      }
    }
  }

  public syncTermsFromTermStore = () => {
    if (!this.state.selectedTermSet || this.state.selectedTermSet === " ") {
      this.setState({
        isLoading: false
      }, () => {
        this.props.reload();
      });
    } else {
      CategoryService.syncTerms(this.props.environment, this.props.instance, this.props.tenant, this.props.token, this.state.selectedTermGroup, this.state.selectedTermSet, this.props.site, this.props.application).then(() => {
        this.setState({
          isLoading: false,
          isSynching: false
        });
        this.props.reload();
      }, () => {
        this.setState({ isSynching: false, isLoading: false });
      });
    }
  }

  public render(): JSX.Element {
    const noTermsAvailable = !this.state.termGroupList || this.state.termGroupList.length === 0;
    let content: JSX.Element = (
      <div
        className={styles.IA_syncPanel}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start"
          }}
        >
          {this.localizationService.strings.Categories_Description}
          <IAButton
            label={this.localizationService.strings.Categories_TermStoreLink}
            iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Open/SVG/ic_fluent_open_48_regular.svg"
            buttonColor={"#eeeeee"}
            borderRadius={5}
            style={{
              marginTop: 10
            }}
            onClick={() => {
              window.open(`https://${location.host}/_layouts/15/SiteAdmin.aspx#/termStoreAdminCenter`, "_blank");
            }}
          />
        </div>
        {this.state.isLoading ? <IASpinner color={this.props.highlightColor}/> :
        <>
          <IADropDown
            label={this.localizationService.strings.Categories_SelectTermGroup}
            options={this.termGroupOptions}
            onChange={(option) => {
              this.setState({ selectedTermGroup: option, selectedTermSet: undefined }, () => {
                this.loadTermSetsFromGroup(option);
              });
            }}
            selectedOption={this.termGroupOptions ? this.state.selectedTermGroup : undefined}
            borderRadius={5}
            highlightColor={this.props.highlightColor}
            disabled={noTermsAvailable}
            style={{
              opacity: noTermsAvailable ? 0.5 : 1
            }}
          />
          <IADropDown
            label={this.localizationService.strings.Categories_SelectTermSet}
            options={this.termSetOptions}
            onChange={(option) => {
              let selectedTermSet = this.termSetOptions.filter(x => x.key === option)[0];
              if (selectedTermSet) {
                this.setState({ selectedTermSet: selectedTermSet.value });
              }
            }}
            selectedOption={this.termSetOptions ? this.state.selectedTermSet : undefined}
            borderRadius={5}
            highlightColor={this.props.highlightColor}
            disabled={!this.state.selectedTermGroup}
            style={{
              opacity: !this.state.selectedTermGroup ? 0.5 : 1
            }}
          />
          <IAButton
            label={this.localizationService.strings.Categories_SaveSubjects}
            iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Save/SVG/ic_fluent_save_28_regular.svg"
            buttonColor={this.props.highlightColor}
            showSpinner={this.state.isSynching}
            onClick={() => {
              this.setState({ isSynching: true }, () => this.syncTermsFromTermStore());
            }}
            style={{
              float: "left",
              marginTop: 20
            }}
            disbaled={this.state.isSynching || this.state.selectedTermGroup == undefined || this.state.selectedTermSet == undefined}
            borderRadius={5}
          />
        </>
      }
      </div>
    );
    return (
      <IAPanel
        showNavigationBar={true}
        title={this.localizationService.strings.Categorier_SyncCategories}
        open={this.props.show}
        transition="slide"
        isMobile={false}
        width={600}
        close={() => this.props.onDismiss()}
      >
        {content}
      </IAPanel>
    );
  }
}